import {
  INFRA_CURRENCIES,
  RecordInfraPaymentInputSchema,
  type InfraCurrency,
  type InfraService,
} from '@carsfinders/contracts';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

import { t } from '../../../lib/i18n';
import { kyivToday } from '../../analytics/format';
import { financeErrorMessage } from '../../finance/api';
import { useRecordInfraPayment, type RecordInfraPaymentResult } from '../api';
import { formatDate, formatMoney } from '../format';
import { useModal } from '../use-modal';

export interface PayTarget {
  service: Pick<InfraService, 'id' | 'name' | 'amount' | 'currency'>;
  /** The due date being settled, when opened from «Найближчі платежі». */
  dueDate?: string;
  amount?: number;
  currency?: InfraCurrency;
}

/** A fresh key per opened dialog; a retry inside it replays the same payment. */
function newIdempotencyKey(): string {
  return globalThis.crypto.randomUUID();
}

/** The sentence the live region announces once the payment is recorded. */
export function paymentResultText(result: RecordInfraPaymentResult): string {
  const paid = formatMoney(result.payment.amount, result.payment.currency);
  if (!result.service.active) return t('admin.infraCosts.pay.doneArchived', { amount: paid });
  if (result.service.nextDueDate) {
    return t('admin.infraCosts.pay.doneNext', { amount: paid, date: formatDate(result.service.nextDueDate) });
  }
  return t('admin.infraCosts.pay.done', { amount: paid });
}

const INPUT =
  'h-11 w-full rounded-cf-md border border-cf-line bg-cf-surface px-3 text-[14px] text-cf-ink focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-cf-ink aria-[invalid=true]:border-cf-danger';

/**
 * «Оплачено» — records one payment: the date (today, Kyiv, by default), the
 * amount and currency (the expected ones by default) and an optional note.
 * The outcome is announced through an `aria-live` region.
 */
export function InfraPayDialog({ target, onClose }: { target: PayTarget; onClose: () => void }) {
  const [idempotencyKey] = useState(newIdempotencyKey);
  const [paidAt, setPaidAt] = useState(() => kyivToday());
  const [amount, setAmount] = useState(() => String(target.amount ?? target.service.amount));
  const [currency, setCurrency] = useState<InfraCurrency>(target.currency ?? target.service.currency);
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<{ paidAt?: string; amount?: string; note?: string }>({});
  const mutation = useRecordInfraPayment();
  const { ref, onKeyDown } = useModal(onClose, mutation.isPending);
  const titleId = useId();
  const id = useId();

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const parsedAmount = amount.trim() === '' ? Number.NaN : Number(amount.replace(',', '.'));
    const result = RecordInfraPaymentInputSchema.safeParse({
      paidAt,
      amount: parsedAmount,
      currency,
      note: note.trim() || null,
    });
    if (!result.success) {
      const next: typeof errors = {};
      for (const issue of result.error.issues) {
        const field = String(issue.path[0]);
        if (field === 'paidAt') next.paidAt = t('admin.infraCosts.form.errors.invalidDate');
        if (field === 'amount') next.amount = t('admin.infraCosts.form.errors.invalidNumber');
        if (field === 'note') next.note = t('admin.infraCosts.form.errors.tooLong');
      }
      setErrors(next);
      return;
    }
    setErrors({});
    mutation.mutate({ id: target.service.id, input: result.data, idempotencyKey });
  };

  const done = mutation.data;
  const failure = mutation.error;
  const closeRef = useRef<HTMLButtonElement>(null);
  // The form unmounts on success: keep focus inside the dialog, on «Закрити».
  useEffect(() => {
    if (done) closeRef.current?.focus();
  }, [done]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-cf-ink/35 p-4 backdrop-blur-[2px]"
      onClick={(event) => {
        if (event.target === event.currentTarget && !mutation.isPending) onClose();
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
        data-testid="infra-pay-dialog"
        className="w-full max-w-md rounded-cf-lg border border-cf-line bg-cf-surface p-5 shadow-xl"
      >
        <h2 id={titleId} className="text-[17px] font-semibold tracking-[-0.01em] text-cf-ink">
          {t('admin.infraCosts.pay.title', { service: target.service.name })}
        </h2>
        {target.dueDate && (
          <p className="mt-1 text-[13px] text-cf-ink-muted">
            {t('admin.infraCosts.pay.dueDate', { date: formatDate(target.dueDate) })}
          </p>
        )}

        {/* Announced politely: success and refusal both land here. */}
        <div aria-live="polite" role="status" data-testid="infra-pay-result" className="mt-3 empty:hidden">
          {done && (
            <p className="rounded-cf-md border border-cf-success bg-cf-success-tint px-3 py-2 text-[13px] text-cf-ink">
              {paymentResultText(done)}
            </p>
          )}
          {failure && (
            <p className="rounded-cf-md border border-cf-danger bg-cf-danger-tint px-3 py-2 text-[13px] text-cf-danger">
              {financeErrorMessage(failure) ?? t('admin.infraCosts.pay.failed')}
            </p>
          )}
        </div>

        {done ? (
          <div className="mt-5 flex justify-end">
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              className="h-10 rounded-cf-md bg-cf-brand px-4 text-[14px] font-semibold text-white hover:bg-cf-brand-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-ink"
            >
              {t('admin.infraCosts.pay.close')}
            </button>
          </div>
        ) : (
          <form noValidate onSubmit={onSubmit} className="mt-4 flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label htmlFor={`${id}-paidAt`} className="text-[12px] font-semibold text-cf-ink-muted">
                {t('admin.infraCosts.pay.paidAt')}
              </label>
              <input
                id={`${id}-paidAt`}
                name="paidAt"
                type="date"
                data-autofocus
                value={paidAt}
                onChange={(e) => setPaidAt(e.target.value)}
                aria-invalid={Boolean(errors.paidAt)}
                aria-describedby={errors.paidAt ? `${id}-paidAt-error` : undefined}
                className={`${INPUT} font-mono`}
              />
              {errors.paidAt && (
                <p id={`${id}-paidAt-error`} className="text-[12px] text-cf-danger">
                  {errors.paidAt}
                </p>
              )}
            </div>
            <div className="grid grid-cols-[1fr_110px] gap-3">
              <div className="flex flex-col gap-1">
                <label htmlFor={`${id}-amount`} className="text-[12px] font-semibold text-cf-ink-muted">
                  {t('admin.infraCosts.pay.amount')}
                </label>
                <input
                  id={`${id}-amount`}
                  name="amount"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  aria-invalid={Boolean(errors.amount)}
                  aria-describedby={errors.amount ? `${id}-amount-error` : undefined}
                  className={`${INPUT} font-mono`}
                />
                {errors.amount && (
                  <p id={`${id}-amount-error`} className="text-[12px] text-cf-danger">
                    {errors.amount}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor={`${id}-currency`} className="text-[12px] font-semibold text-cf-ink-muted">
                  {t('admin.infraCosts.pay.currency')}
                </label>
                <select
                  id={`${id}-currency`}
                  name="currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value as InfraCurrency)}
                  className={INPUT}
                >
                  {INFRA_CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={`${id}-note`} className="text-[12px] font-semibold text-cf-ink-muted">
                {t('admin.infraCosts.pay.note')}
              </label>
              <textarea
                id={`${id}-note`}
                name="note"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                aria-invalid={Boolean(errors.note)}
                aria-describedby={errors.note ? `${id}-note-error` : undefined}
                className={`${INPUT} h-auto py-2`}
              />
              {errors.note && (
                <p id={`${id}-note-error`} className="text-[12px] text-cf-danger">
                  {errors.note}
                </p>
              )}
            </div>
            <div className="mt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={mutation.isPending}
                className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-4 text-[14px] font-semibold text-cf-ink focus-visible:outline-2 focus-visible:outline-cf-ink"
              >
                {t('admin.infraCosts.pay.cancel')}
              </button>
              <button
                type="submit"
                disabled={mutation.isPending}
                data-testid="infra-pay-submit"
                className="h-10 rounded-cf-md bg-cf-brand px-4 text-[14px] font-semibold text-white hover:bg-cf-brand-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-ink disabled:opacity-40"
              >
                {mutation.isPending ? t('admin.infraCosts.pay.saving') : t('admin.infraCosts.pay.submit')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
